import { html, css } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { EwcBase } from '../base/ewc-base';
import { emit, animateTo, prefersReducedMotion } from '../../adapters/webawesome-adapter';

export type ButtonType = 'button' | 'submit' | 'reset';
export type ButtonVariant = 'filled' | 'outlined' | 'text' | 'danger' | 'success';
export type ButtonTarget = '_blank' | '_parent' | '_self' | '_top';

@customElement('ewc-button')
export class EwcButton extends EwcBase {
  @query('button, a') button!: HTMLButtonElement | HTMLAnchorElement;
  
  @property({ type: String })
  type: ButtonType = 'button';

  @property({ type: String })
  buttonVariant: ButtonVariant = 'filled';

  @property({ type: Boolean })
  loading = false;

  @property({ type: String })
  icon = '';

  @property({ type: String })
  iconPosition: 'left' | 'right' = 'left';
  
  @property({ type: Boolean })
  pill = false;
  
  @property({ type: Boolean })
  circle = false;
  
  @property({ type: String })
  href = '';
  
  @property({ type: String })
  target: ButtonTarget = '_self';
  
  @property({ type: String })
  download = '';
  
  @property({ type: Boolean, attribute: 'icon-only' })
  iconOnly = false;
  
  @property({ type: Boolean })
  caret = false;
  
  @property({ type: String, attribute: 'loading-text' })
  loadingText = '';

  static override get styles() {
    return [
      ...EwcBase.styles,
      css`
        :host {
          display: inline-block;
        }

        button,
        a {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: var(--ewc-space-2);
          padding: var(--ewc-padding-y) var(--ewc-padding-x);
          min-height: var(--ewc-height);
          font-size: var(--ewc-font-size);
          line-height: var(--ewc-line-height);
          font-family: inherit;
          font-weight: 500;
          border: 1px solid transparent;
          border-radius: var(--ewc-radius-md);
          cursor: pointer;
          position: relative;
          overflow: hidden;
          white-space: nowrap;
          text-decoration: none;
          transition-property: color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: var(--ewc-transition-fast);
          box-shadow: var(--ewc-shadow-sm);
        }

        button:focus,
        a:focus {
          outline: 2px solid transparent;
          outline-offset: 2px;
        }
        
        button:focus-visible,
        a:focus-visible {
          outline: none;
          box-shadow: 0 0 0 2px var(--ewc-bg-color), 0 0 0 4px var(--ewc-color);
        }

        /* Filled variant */
        :host([buttonVariant="filled"]) button,
        :host([buttonVariant="filled"]) a {
          background-color: var(--ewc-color);
          color: var(--ewc-text-color);
          border-color: transparent;
        }

        :host([buttonVariant="filled"]) button:hover:not(:disabled),
        :host([buttonVariant="filled"]) a:hover:not([aria-disabled="true"]) {
          background-color: var(--ewc-color-hover);
          transform: translateY(-1px);
          box-shadow: var(--ewc-shadow-md);
        }

        :host([buttonVariant="filled"]) button:active:not(:disabled),
        :host([buttonVariant="filled"]) a:active:not([aria-disabled="true"]) {
          background-color: var(--ewc-color-active);
          transform: translateY(0);
          box-shadow: var(--ewc-shadow-sm);
        }

        /* Outlined variant */
        :host([buttonVariant="outlined"]) button,
        :host([buttonVariant="outlined"]) a {
          background-color: var(--ewc-bg-color);
          color: var(--ewc-color-700);
          border-color: var(--ewc-border-color);
          box-shadow: var(--ewc-shadow-sm);
        }

        :host([buttonVariant="outlined"]) button:hover:not(:disabled),
        :host([buttonVariant="outlined"]) a:hover:not([aria-disabled="true"]) {
          background-color: var(--ewc-color-50);
          border-color: var(--ewc-color-300);
          color: var(--ewc-color-800);
        }

        /* Text variant */
        :host([buttonVariant="text"]) button,
        :host([buttonVariant="text"]) a {
          background-color: transparent;
          color: var(--ewc-color-600);
          border-color: transparent;
          box-shadow: none;
        }

        :host([buttonVariant="text"]) button:hover:not(:disabled),
        :host([buttonVariant="text"]) a:hover:not([aria-disabled="true"]) {
          background-color: var(--ewc-color-50);
          color: var(--ewc-color-700);
        }

        /* Danger variant */
        :host([buttonVariant="danger"]) button,
        :host([buttonVariant="danger"]) a {
          background-color: #dc2626;
          color: white;
          border-color: transparent;
        }

        :host([buttonVariant="danger"]) button:hover:not(:disabled),
        :host([buttonVariant="danger"]) a:hover:not([aria-disabled="true"]) {
          background-color: #b91c1c;
        }

        /* Success variant */
        :host([buttonVariant="success"]) button,
        :host([buttonVariant="success"]) a {
          background-color: #16a34a;
          color: white;
          border-color: transparent;
        }

        :host([buttonVariant="success"]) button:hover:not(:disabled),
        :host([buttonVariant="success"]) a:hover:not([aria-disabled="true"]) {
          background-color: #15803d;
        }

        /* Disabled state */
        button:disabled,
        a[aria-disabled="true"] {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
          pointer-events: none;
        }

        /* Pill shape */
        :host([pill]) button,
        :host([pill]) a {
          border-radius: var(--ewc-radius-full);
        }

        /* Circle shape */
        :host([circle]) button,
        :host([circle]) a {
          border-radius: 50%;
          padding: var(--ewc-padding-y);
          width: var(--ewc-height);
          min-width: var(--ewc-height);
        }

        /* Icon-only adjustments */
        :host([icon-only]) button,
        :host([icon-only]) a {
          padding: var(--ewc-padding-y);
          min-width: var(--ewc-height);
        }

        /* Loading state */
        :host([loading]) button,
        :host([loading]) a {
          position: relative;
          pointer-events: none;
        }

        :host([loading]:not([loading-text])) button,
        :host([loading]:not([loading-text])) a {
          color: transparent;
        }

        .spinner {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 1rem;
          height: 1rem;
        }

        .spinner svg {
          animation: spin 1s linear infinite;
        }

        .spinner circle {
          stroke: currentColor;
          stroke-opacity: 0.25;
        }

        .spinner path {
          stroke: currentColor;
          stroke-linecap: round;
        }

        :host([buttonVariant="filled"]) .spinner,
        :host([buttonVariant="danger"]) .spinner,
        :host([buttonVariant="success"]) .spinner {
          color: white;
        }

        :host([buttonVariant="outlined"]) .spinner,
        :host([buttonVariant="text"]) .spinner {
          color: var(--ewc-color);
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Icon styles */
        .icon {
          font-size: 1.25em;
          line-height: 1;
          flex-shrink: 0;
        }

        /* Caret icon */
        .caret {
          font-size: 0.75em;
          margin-left: var(--ewc-space-2);
          transition: transform var(--ewc-transition-fast);
        }

        :host([aria-expanded="true"]) .caret {
          transform: rotate(180deg);
        }

        /* Ripple effect */
        .ripple {
          position: absolute;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.5);
          transform: scale(0);
          animation: ripple 0.6s ease-out;
        }

        @keyframes ripple {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
      `
    ];
  }

  render() {
    const isLink = !!this.href;
    const isDisabled = this.disabled || this.loading;
    
    const content = html`
      ${this.loading ? html`
        <span class="spinner">
          <svg fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor"></path>
          </svg>
        </span>
      ` : ''}
      ${this.icon && this.iconPosition === 'left' && !this.loading ? html`<span class="icon">${this.icon}</span>` : ''}
      ${this.loading && this.loadingText ? html`<span>${this.loadingText}</span>` : html`<slot></slot>`}
      ${this.icon && this.iconPosition === 'right' && !this.loading ? html`<span class="icon">${this.icon}</span>` : ''}
      ${this.caret ? html`<span class="caret">▼</span>` : ''}
    `;

    return isLink ? html`
      <a
        href="${this.disabled ? 'javascript:void(0)' : this.href}"
        target="${this.target}"
        download="${this.download || ''}"
        rel="${this.target === '_blank' ? 'noopener noreferrer' : ''}"
        aria-disabled="${isDisabled}"
        @click="${this._handleClick}"
        part="button"
        class=${classMap({
          button: true,
          'button--disabled': isDisabled
        })}
      >
        ${content}
      </a>
    ` : html`
      <button
        type="${this.type}"
        ?disabled="${isDisabled}"
        @click="${this._handleClick}"
        part="button"
        class="button"
      >
        ${content}
      </button>
    `;
  }

  private async _handleClick(e: MouseEvent) {
    if (this.disabled || this.loading) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Create ripple effect
    if (!prefersReducedMotion()) {
      this._createRipple(e);
    }

    // Emit EWC click event
    const event = emit(this, 'ewc-click', { cancelable: true });
    
    if (event.defaultPrevented) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  private _createRipple(e: MouseEvent) {
    const button = this.shadowRoot?.querySelector('button, a') as HTMLElement;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;

    const ripple = document.createElement('span');
    ripple.className = 'ripple';
    ripple.style.width = ripple.style.height = size + 'px';
    ripple.style.left = x + 'px';
    ripple.style.top = y + 'px';

    button.appendChild(ripple);
    
    animateTo(ripple, [
      { transform: 'scale(0)', opacity: 1 },
      { transform: 'scale(4)', opacity: 0 }
    ], {
      duration: 600,
      easing: 'ease-out'
    }).then(() => ripple.remove());
  }

  // Public methods
  click() {
    this.button?.click();
  }

  focus(options?: FocusOptions) {
    this.button?.focus(options);
  }

  blur() {
    this.button?.blur();
  }

  get form() {
    return this.closest('form');
  }

  submit() {
    if (this.type === 'submit' && this.form) {
      this.form.requestSubmit();
    }
  }
}